import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import * as Icons from 'lucide-react';

const EAuctionProcessModal = ({ isOpen, onClose, onSave, principle }) => {
    const [formData, setFormData] = useState({
        icon: 'Eye',
        title: '',
        color: 'blue',
        is_active: true
    });

    const [loading, setLoading] = useState(false);

    // Available Lucide icons 
    const availableIcons = ['Eye', 'Shield', 'FileText', 'Gavel', 'MapPin', 'Calendar', 'CheckCircle2', 'TriangleAlert', 'User', 'Building2'];
    const availableColors = ['blue', 'green', 'gold', 'red', 'purple', 'gray'];

    useEffect(() => {
        if (principle) {
            setFormData({
                icon: principle.icon || 'Eye',
                title: principle.title || '',
                color: principle.color || 'blue',
                is_active: principle.is_active !== undefined ? principle.is_active : true
            });
        } else {
            // Reset for Add New
            setFormData({
                icon: 'Eye',
                title: '',
                color: 'blue',
                is_active: true
            });
        }
    }, [principle, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSelectChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }

        setLoading(true);
        // Simulate a small delay for better UX
        setTimeout(() => {
            onSave(formData);
            setLoading(false);
            onClose();
        }, 300);
    };

    const renderIcon = (iconName) => {
        const Icon = Icons[iconName];
        return Icon ? <Icon className="w-5 h-5" /> : null;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
            <div className="relative min-h-screen flex items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl animate-in zoom-in duration-200">
                    {/* Header */}
                    <div className="bg-white px-6 py-4 flex items-center justify-between rounded-t-2xl border-b border-slate-100">
                        <h2 className="text-xl font-bold text-slate-800">
                            {principle ? 'Edit Principle' : 'Add New Principle'}
                        </h2>
                        <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                            <XMarkIcon className="h-6 w-6 text-slate-500" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-6">

                        {/* Title Input */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Principle Title <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                placeholder="e.g. Transparent Bidding"
                            />
                        </div>

                        {/* Icon Selection - Visual Grid */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-3">Select Icon</label>
                            <div className="grid grid-cols-5 gap-3">
                                {availableIcons.map(icon => (
                                    <button
                                        key={icon}
                                        type="button"
                                        onClick={() => handleSelectChange('icon', icon)}
                                        className={`p-3 rounded-xl border transition-all flex flex-col items-center gap-2 ${formData.icon === icon
                                            ? 'border-primary-500 bg-primary-50 text-primary-600 ring-1 ring-primary-500 shadow-sm'
                                            : 'border-slate-100 bg-slate-50/50 text-slate-400 hover:border-slate-300 hover:bg-white'
                                            }`}
                                    >
                                        {renderIcon(icon)}
                                        <span className="text-[10px] font-medium truncate w-full text-center">{icon}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Color Selection & Active Status */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-3">Select Color Theme</label>
                                <div className="flex flex-wrap gap-3">
                                    {availableColors.map(color => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => handleSelectChange('color', color)}
                                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all border capitalize flex items-center gap-2 ${formData.color === color
                                                ? `bg-${color}-100 text-${color}-700 border-${color}-300 ring-2 ring-${color}-200`
                                                : `bg-white text-slate-600 border-slate-200 hover:bg-slate-50`
                                                }`}
                                        >
                                            <span className={`inline-block w-2.5 h-2.5 rounded-full bg-${color}-500 shadow-sm`}></span>
                                            {color}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-3">Status</label>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                                    className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border transition-all ${formData.is_active
                                        ? 'bg-green-50 border-green-200 text-green-700'
                                        : 'bg-slate-50 border-slate-200 text-slate-500'
                                        }`}
                                >
                                    <span className="font-semibold text-sm">{formData.is_active ? 'Active' : 'Inactive'}</span>
                                    <div className={`w-10 h-6 rounded-full relative transition-all ${formData.is_active ? 'bg-green-500' : 'bg-slate-300'}`}>
                                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${formData.is_active ? 'left-5' : 'left-1'}`}></div>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-6 border-t border-slate-100">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-6 py-3 border border-slate-200 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-lg hover:shadow-xl"
                            >
                                {loading ? 'Saving...' : 'Save Principle'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EAuctionProcessModal;
